import styles from 'features/referral/Referral.module.scss'
import ReferralForm from 'features/referral/components/referral-form/ReferralForm'
import { Trans, useTranslation } from 'next-i18next/pages'
import parse from 'html-react-parser'
import { ReactElement, useEffect, useState } from 'react'
import {
  Button,
  CallToAction,
  Card,
  Cards,
  Drawer,
  Grid,
  Header,
  Heading,
  List,
  StepList,
  Tag,
  TextAndMedia
} from '@boltenergy-be/design-system'
import relativeTime from 'dayjs/plugin/relativeTime'
import dayjs from 'dayjs'
import { DAYJS_THRESHOLDS, REFERRAL_AMOUNT_GIVE, REFERRAL_AMOUNT_RECEIVE } from 'features/referral/Referral.constants'
import DurationTimeStamp from 'components/duration-time-stamp/DurationTimeStamp'
import { ReferralProps } from 'features/referral/types'
import { BlocksContent } from '@strapi/blocks-react-renderer'
import BlocksRenderer from 'components/blocks-renderer/BlocksRenderer'
import FaqWithDefaultDescription from 'components/faq-with-default-description/FaqWithDefaultDescription'
import ReferralCodeTermsDrawer from 'components/terms-and-conditions-drawers/referral-code-terms/ReferralCodeTermsDrawer'
import Link from 'components/link/Link'
import { getPortalUrl } from 'utils/tracking'
import InfluencerCarousel from 'features/referral/components/influencer-carousel/InfluencerCarousel'
import { useRouter } from 'next/router'

dayjs.extend(relativeTime, {
  thresholds: DAYJS_THRESHOLDS,
  rounding: Math.floor
})

const Referrer = ({ activeAction, faqCategories, videoTestimonials }: ReferralProps) => {
  // i18n
  const { t } = useTranslation('referral')

  // Router
  const { query, isReady } = useRouter()

  // Local state
  const [showActionTerms, setShowActionTerms] = useState<boolean>(false)
  const [showTerms, setShowTerms] = useState<boolean>(false)
  const [initVideo, setInitVideo] = useState<boolean>(false)

  useEffect(() => {
    if (isReady && query.autoplay === 'true' && !initVideo) {
      setInitVideo(true)
    }
  }, [query, isReady, initVideo])

  // Constants
  const amountGive = activeAction?.reduction || REFERRAL_AMOUNT_GIVE
  const amountReceive = activeAction?.reduction || REFERRAL_AMOUNT_RECEIVE

  return (
    <main>
      <Header
        id="hero"
        title={activeAction?.websiteHero?.title || parse(t('referrer.hero.title', { amountGive, amountReceive }))}
        color="accent-warm"
        shape="s5"
        tag={
          activeAction?.websiteHero?.tag
            ? {
                type: 'positive',
                icon: 'clock',
                children: (
                  <Link as="a" href="#action" variant="inline" className={styles['tag-link']}>
                    {activeAction.websiteHero.tag}
                  </Link>
                )
              }
            : undefined
        }
      >
        {activeAction?.websiteHero?.description ? (
          <BlocksRenderer content={activeAction?.websiteHero?.description} />
        ) : (
          <p>{parse(t('referrer.hero.paragraph', { amountGive, amountReceive }))}</p>
        )}

        <ReferralForm {...{ activeAction }} />
      </Header>

      {/*	How it works */}
      <TextAndMedia
        title={t('referrer.howItWorks.title')}
        media={{
          type: 'image',
          props: { publicId: 'website/referral/elisabeth_juliet', width: 432, height: 576, alt: '' }
        }}
      >
        <StepList size="large">
          <li className={styles.step}>
            <div>
              <Heading as="h3" variant="h4" serif>
                {t('referrer.howItWorks.one.title')}
              </Heading>
              <p>{parse(t('referrer.howItWorks.one.description'))}</p>
            </div>
          </li>

          <li className={styles.step}>
            <div>
              <Heading as="h3" variant="h4" serif>
                {t('referrer.howItWorks.two.title')}
              </Heading>
              <p>
                <Trans
                  t={t}
                  i18nKey="referrer.howItWorks.two.description"
                  components={[
                    <Link key="0" as="a" variant="inline" suppressHydrationWarning href={getPortalUrl('/friends')} target="_blank" />
                  ]}
                />
              </p>
            </div>
          </li>

          <li className={styles.step}>
            <div>
              <Heading as="h3" variant="h4" serif>
                {t('referrer.howItWorks.three.title', { amountReceive, amountGive })}
              </Heading>
              <p>
                <Trans
                  t={t}
                  i18nKey="referrer.howItWorks.three.description"
                  values={{ amountReceive }}
                  components={[<Button key="0" representation="link" variant="inline" onClick={() => setShowTerms(true)} />]}
                />
              </p>
            </div>
          </li>
        </StepList>

        <ReferralCodeTermsDrawer isOpen={showTerms} onRequestClose={() => setShowTerms(false)} />
      </TextAndMedia>

      {/* HERO Action */}
      {!!activeAction && (
        <>
          <TextAndMedia
            id="action"
            inset
            inverse
            color="accent-vibrant"
            title={activeAction?.shortContent?.title}
            aboveTitleSlot={
              <Tag type="positive" icon="clock">
                <DurationTimeStamp endDate={activeAction?.end} />
              </Tag>
            }
            showMediaOnMobile
            classnames={{ container: styles['action-container'] }}
            media={
              activeAction?.video
                ? {
                    type: 'video',
                    props: {
                      publicId: activeAction?.video?.provider_metadata?.public_id,
                      thumbnail: {
                        publicId: activeAction?.image?.provider_metadata?.public_id,
                        fill: true,
                        alt: '',
                        className: styles['action-media']
                      },
                      initVideoWithoutClick: initVideo
                    }
                  }
                : {
                    type: 'image',
                    props: {
                      publicId: activeAction?.image?.provider_metadata?.public_id,
                      fill: true,
                      alt: '',
                      className: styles['action-media']
                    }
                  }
            }
          >
            <div className={styles['action-description']}>
              <BlocksRenderer content={activeAction?.shortContent?.description as BlocksContent} />
              <Button representation="link" variant="inline" onClick={() => setShowActionTerms(true)}>
                {t('referrer.action.drawerTrigger')}.
              </Button>
            </div>

            {/* More info */}
            <Link representation="button" as="a" href="#hero" variant="layered" trailingIcon="chevronRight">
              {t('referrer.form.generate')}
            </Link>
          </TextAndMedia>

          <Drawer isOpen={showActionTerms} onRequestClose={() => setShowActionTerms(false)} title={activeAction?.conditions?.title}>
            <BlocksRenderer
              content={activeAction.conditions.description as BlocksContent}
              blocks={{
                heading: ({ children, level }) => (
                  <Heading as={`h${level}`} variant={level === 1 ? 'h2' : 'h5'} serif>
                    {children}
                  </Heading>
                ),
                list: ({ children, format }) => <List as={format === 'ordered' ? 'ol' : 'ul'}>{children as ReactElement[]}</List>
              }}
            />
          </Drawer>
        </>
      )}

      {!!videoTestimonials?.length && <InfluencerCarousel testimonials={videoTestimonials} />}

      <Grid color="accent-warm" title={t('referrer.merch.title')} description={t('referrer.merch.description')}>
        {(t('referrer.merch.rewards', { returnObjects: true }) as { title: string; description: string }[]).map((reward, index) => (
          <Card key={`reward-${index}`} className={styles['merch-card']}>
            <Heading as="h2" variant="h4" serif>
              {reward.title}
            </Heading>
            <p>{parse(reward.description)}</p>
          </Card>
        ))}
      </Grid>

      <CallToAction
        color="accent-vibrant"
        shape="s1"
        title={t('referrer.cta.title')}
        action={
          <Link representation="button" as="a" variant="layered" trailingIcon="chevronRight" href="#hero">
            {t('referrer.cta.action')}
          </Link>
        }
      >
        <p>{t('referrer.cta.description')}</p>
      </CallToAction>

      <Cards title={t('referrer.pastActions.title')} description={t('referrer.pastActions.description')}>
        {(t('referrer.pastActions.actions', { returnObjects: true }) as { title: string; description: string }[]).map((action) => (
          <Cards.Card key={action.title} title={action.title}>
            <p>{parse(action.description)}</p>
          </Cards.Card>
        ))}
      </Cards>

      {/* FAQ */}
      <FaqWithDefaultDescription categories={faqCategories} title={t('referral:faq.title')} />
    </main>
  )
}

export default Referrer
